import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Button,
  Copy,
  Flex,
  Heading,
  Loader,
  Square,
  Stack,
  SvgMagnifyingGlass,
  SvgX,
} from '@grubhubprod/gh-cookbook-react';
import classnames from 'classnames';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { Column, Row, Squish } from '@grubhubprod/gh-cookbook-react/dist';
import { Modal } from 'src/components/Modal/Modal';
import { useDebouncedValue } from 'src/utilities/useDebouncedValue';
import {
  IGroup,
  ISearchParams,
  SearchStatus,
  useGroupDinersSearch,
} from 'src/utilities/useSearchHooks';
import { Input } from 'src/components/Input/Input';
import Table from 'src/components/Table/Table';
import { Checkbox } from 'src/components/Checkbox/Checkbox';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import { AsyncTaskStatus, useAsyncTask } from 'src/utilities/useAsyncTask';
import { sdk } from 'src/utilities/sdk';
import {
  pushError,
  pushErrorFromAPI,
  pushSuccess,
} from 'src/utilities/notifications';

const DEBOUNCE_SEARCH_TIMING = 200;

enum ManageEmployeesModalTab {
  ADDED,
  NOT_ADDED,
}

export enum ParticipantTypes {
  ALL = 'ALL',
  MEMBERS_ONLY = 'MEMBERS_ONLY',
  NON_MEMBERS_ONLY = 'NON_MEMBERS_ONLY',
}

export interface IManageEmployeesModalProps {
  onSuccess: () => void;
  corpId: string;
  onClose: () => void;
  group: IGroup;
  addEmployeesMode?: boolean;
}

export function ManageEmployeesModal({
  corpId,
  onClose,
  onSuccess,
  group,
  addEmployeesMode,
}: IManageEmployeesModalProps): JSX.Element {
  const { status: asyncTaskStatus, startAsyncTask } = useAsyncTask();
  const [activeTab, setActiveTab] = useState<ManageEmployeesModalTab>(
    ManageEmployeesModalTab.NOT_ADDED
  );
  const {
    resultsList: diners,
    totalResults,
    searchStatus: dinersSearchStatus,
    hasMoreResults,
    loadMoreNumber,
    doSearch,
    hasNoResultsWithQuery,
    hasNoResultsWithoutQuery,
  } = useGroupDinersSearch(corpId, group.id);

  const { register, watch, formState, handleSubmit, trigger, setValue } =
    useForm({
      mode: 'onChange',
      defaultValues: { searchTerm: '', dinersSelected: [] },
    });

  const { isSubmitting } = formState;

  const debouncedSearchTerm = useDebouncedValue(
    watch('searchTerm', '') as string,
    DEBOUNCE_SEARCH_TIMING
  );

  const [dinersSelected, setDinersSelected] = useState<string[]>([]);

  useEffect(() => {
    getDiners(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, activeTab]);

  useEffect(() => {
    if (asyncTaskStatus === AsyncTaskStatus.COMPLETE) {
      onSuccess();
      onClose();
      pushSuccess(`The group ${group.name} has been updated`);
    }

    if (asyncTaskStatus === AsyncTaskStatus.ERROR) {
      pushError(`The group ${group.name} has not been updated. Try again.`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.name, asyncTaskStatus]);

  async function getDiners(isLoadingMore = false) {
    const params: ISearchParams = {
      filter_by:
        activeTab === ManageEmployeesModalTab.ADDED
          ? ParticipantTypes.MEMBERS_ONLY
          : ParticipantTypes.NON_MEMBERS_ONLY,
      include_diners_not_in_tag: true,
    };

    if (debouncedSearchTerm) {
      params.query = debouncedSearchTerm;
    }
    await doSearch(params, isLoadingMore);
    setValue(
      'dinersSelected',
      dinersSelected.length === 0 ? null : dinersSelected
    );
    await trigger();
  }

  function dinerClickedHandle(e: ChangeEvent<HTMLInputElement>) {
    const isChecked = e.target.checked;
    const value = e.target.value;
    const diners = isChecked
      ? [...dinersSelected, value]
      : dinersSelected.filter((id) => id !== value);
    setDinersSelected(diners);
  }

  async function onFormSubmit() {
    const payload = {
      diners_to_add:
        activeTab === ManageEmployeesModalTab.NOT_ADDED ? dinersSelected : [],
      diners_to_remove:
        activeTab === ManageEmployeesModalTab.ADDED ? dinersSelected : [],
    };
    try {
      await startAsyncTask(() => {
        return sdk.http.put(
          `/corp-admin/${corpId}/groups/v2/${group.id}/diners`,
          payload
        );
      });
    } catch (error) {
      pushErrorFromAPI(error);
    }
  }

  function getButtonCopy() {
    const selectedEmployeeCount = dinersSelected.length;
    const action =
      activeTab === ManageEmployeesModalTab.ADDED ? 'Remove' : 'Add';
    switch (selectedEmployeeCount) {
      case 0: {
        return `${action} employees`;
      }
      case 1: {
        return `${action} 1 employee`;
      }
      default:
        return `${action} ${selectedEmployeeCount} employees`;
    }
  }

  return (
    <Modal size="sm">
      <Square
        as="header"
        dimension="2"
        width="100%"
        zIndex={1}
        position="relative"
      >
        <Squish inset="x-sm">
          <Button
            variant="tertiary"
            type="button"
            className="closeModalButton"
            onClick={onClose}
          >
            <SvgX aria-label="close" size="24px" />
          </Button>
        </Squish>
        <Squish inset="lg">
          <Stack y="x-sm">
            <Heading variant="h1">
              {addEmployeesMode ? 'Add' : 'Manage'} employees in {group.name}
            </Heading>
          </Stack>
          {!addEmployeesMode && (
            <Stack y="md">
              <Flex justifyContent="start" alignItems="center">
                <Stack
                  x="lg"
                  onClick={() => {
                    setActiveTab(ManageEmployeesModalTab.ADDED);
                    setDinersSelected([]);
                  }}
                  className={classnames('', {
                    navBarLinkDesktopActive:
                      activeTab === ManageEmployeesModalTab.ADDED,
                  })}
                >
                  <Square
                    inset="md"
                    className="navBarLink"
                    minWidth="150px"
                    role="button"
                    display="flex"
                    justifyContent="center"
                  >
                    <Copy variant="body">Added</Copy>
                  </Square>
                </Stack>
                <Stack
                  x="lg"
                  onClick={() => {
                    setActiveTab(ManageEmployeesModalTab.NOT_ADDED);
                    setDinersSelected([]);
                  }}
                  className={classnames('', {
                    navBarLinkDesktopActive:
                      activeTab === ManageEmployeesModalTab.NOT_ADDED,
                  })}
                >
                  <Square
                    inset="md"
                    className="navBarLink"
                    minWidth="150px"
                    role="button"
                    display="flex"
                    justifyContent="center"
                  >
                    <Copy variant="body">Not added</Copy>
                  </Square>
                </Stack>
              </Flex>
            </Stack>
          )}
          <Stack y="x-sm">
            <Row>
              <Column phone={6}>
                <Input
                  key="searchTerm"
                  ariaLabel="Search by name or email"
                  placeholder="Search by name or email"
                  name="searchTerm"
                  id="searchTerm"
                  prefixIcon={
                    <Copy variant="body" color="secondaryText">
                      <SvgMagnifyingGlass size="16px" />
                    </Copy>
                  }
                  ref={register}
                />
              </Column>
            </Row>
          </Stack>
          <Flex justifyContent="space-between">
            <Copy
              variant="caption"
              color="secondaryText"
            >{`${dinersSelected.length} selected`}</Copy>

            <Copy
              variant="caption"
              color="secondaryText"
            >{`${diners.length} of ${totalResults}`}</Copy>
          </Flex>
        </Squish>
      </Square>
      <Squish inset="lg" maxHeight="300px" minHeight="100px" overflow="auto">
        <>
          {totalResults > 0 && (
            <Table
              showFooter={false}
              hideHeader
              isLoading={
                dinersSearchStatus === SearchStatus.LOADING ||
                asyncTaskStatus === AsyncTaskStatus.IN_PROGRESS
              }
              isSignupOrSetupTable={false}
              body={() => (
                <ul className="removeListStyles">
                  {diners.map((diner) => (
                    <Square
                      as="li"
                      key={diner.id}
                      inset="md"
                      borderBottom="1px solid"
                      borderColor="lightLine"
                    >
                      <Row gutter={8}>
                        <Column phone={1} gutter={8}>
                          <Stack x="x-sm">
                            <Checkbox
                              id={`dinersSelected_${diner.id}`}
                              name="dinersSelected"
                              ariaLabel={`select diner ${diner.id}`}
                              ref={register}
                              value={diner.id}
                              onChange={dinerClickedHandle}
                              cancelLabelBottomMargin
                            />
                          </Stack>
                        </Column>
                        <Column
                          phone={5}
                          gutter={8}
                          className="ellipsisOverflow"
                        >
                          {diner.name}
                        </Column>
                        <Column
                          phone={6}
                          gutter={8}
                          className="ellipsisOverflow"
                        >
                          {diner.email}
                        </Column>
                      </Row>
                    </Square>
                  ))}
                </ul>
              )}
            />
          )}
          {hasNoResultsWithQuery && (
            <NoResultsMsg
              message={`No results matching '${debouncedSearchTerm}'`}
            />
          )}
          {hasMoreResults && (
            <Squish inset="sm">
              <Flex alignItems="center" justifyContent="center">
                {dinersSearchStatus === SearchStatus.LOADING && <Loader />}
                {dinersSearchStatus === SearchStatus.COMPLETE && (
                  <Button variant="tertiary" onClick={() => getDiners(true)}>
                    Load {loadMoreNumber} more
                  </Button>
                )}
              </Flex>
            </Squish>
          )}
          {hasNoResultsWithoutQuery &&
            activeTab === ManageEmployeesModalTab.ADDED && (
              <NoResultsMsg message="No employees added yet." />
            )}
          {hasNoResultsWithoutQuery &&
            activeTab === ManageEmployeesModalTab.NOT_ADDED && (
              <NoResultsMsg message="All employees have been added." />
            )}
        </>
      </Squish>
      <Squish
        as="footer"
        dimension="2"
        inset="lg"
        zIndex={1}
        position="relative"
      >
        <Flex>
          <Stack x="md">
            <Button
              variant="primary"
              buttonSize="medium"
              disabled={
                !dinersSelected.length ||
                isSubmitting ||
                asyncTaskStatus === AsyncTaskStatus.IN_PROGRESS ||
                asyncTaskStatus === AsyncTaskStatus.COMPLETE
              }
              isLoading={
                isSubmitting || asyncTaskStatus === AsyncTaskStatus.IN_PROGRESS
              }
              onClick={handleSubmit(onFormSubmit)}
            >
              {getButtonCopy()}
            </Button>
          </Stack>
          <Button
            variant="secondary"
            buttonSize="medium"
            disabled={
              isSubmitting ||
              asyncTaskStatus === AsyncTaskStatus.IN_PROGRESS ||
              asyncTaskStatus === AsyncTaskStatus.COMPLETE
            }
            onClick={onClose}
          >
            Cancel
          </Button>
        </Flex>
      </Squish>
    </Modal>
  );
}
